import React, { useEffect, useState, useContext, useMemo } from "react";
import TestimonialsSection from "./TestimonialsSection";
import { saveVisite } from "./../services/visitesServices";
import Diaporama from "./Diaporama";
import { getSettings } from "./../services/settingsServices";
import PartenairesCarousel from "./PartenairesCarousel";
import LastGallerie from "./LastGallerie";
import LastNews from "./LastNews";
import SakuraSeparator from "../components/Separation/SakuraSeparator";
import DOMPurify from "dompurify";
import { getAboutInfo } from "../services/aboutServices";
import ChateauErigoyeSection from "./ChateauErigoyeSection";
import { LanguageContext } from "../contexts/LanguageContext";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const HomePage = () => {
  const [settings, setSettings] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [aboutInfo, setAboutInfo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const { language } = useContext(LanguageContext);

  /* =========================
     Helpers (hoisted-safe)
     ========================= */
  function getLocalizedKey(key) {
    return `${key}_${String(language || "FR").toLowerCase()}`;
  }

  function getFallbackMessage(type) {
    if (language === "FR") {
      return type === "title" ? "Titre indisponible" : "Texte indisponible";
    }
    return type === "title" ? "Title unavailable" : "Text unavailable";
  }

  /* =========================
     i18n cards (advantages)
     ========================= */
  const i18nAdvantages = useMemo(
    () => ({
      FR: {
        title: "Quels sont les avantages du Zen Shiatsu ?",
        cards: [
          {
            title: "Prévention de l’épuisement physique et émotionnel",
            text: "Le Zen Shiatsu agit en profondeur pour apaiser le stress, relâcher les tensions et rééquilibrer l’énergie vitale du corps. Il constitue ainsi un véritable outil de prévention du burn-out, particulièrement adapté aux professionnels exposés à une forte charge mentale (soignants, enseignants, cadres…).",
          },
          {
            title: "Amélioration de la vitalité et de l’équilibre global",
            text: "En stimulant la circulation du Ki (énergie vitale) via les méridiens, le Zen Shiatsu aide à retrouver sérénité, énergie et clarté mentale. Il favorise le bon fonctionnement du système nerveux, la détente musculaire, la respiration, et renforce ainsi le système naturel d’auto-régulation du corps.",
          },
          {
            title: "Reconnexion à soi et mieux-être durable",
            text: "Le Zen Shiatsu crée un espace de calme, d’écoute et de respect où chacun peut se recentrer, libérer ses émotions, et développer une conscience de ses besoins profonds. Il favorise une transformation douce mais durable, tant sur le plan physique que psychologique.",
          },
        ],
      },
      EN: {
        title: "What are the benefits of Zen Shiatsu?",
        cards: [
          {
            title: "Prevention of physical and emotional exhaustion",
            text: "Zen Shiatsu works deeply to soothe stress, release tension, and rebalance the body's vital energy. It is an effective tool to help prevent burnout, particularly suited to professionals facing high mental load (healthcare workers, teachers, managers, etc.).",
          },
          {
            title: "Improved vitality and overall balance",
            text: "By stimulating the flow of Ki (vital energy) through the meridians, Zen Shiatsu helps regain calm, energy, and mental clarity. It supports the nervous system, relaxes muscles, improves breathing, and strengthens the body's natural self-regulation.",
          },
          {
            title: "Reconnection to oneself and lasting well-being",
            text: "Zen Shiatsu creates a space of calm, listening, and respect where everyone can refocus, release emotions, and become aware of deeper needs. It fosters gentle yet lasting change, both physically and psychologically.",
          },
        ],
      },
    }),
    [],
  );

  const adv = i18nAdvantages[language] ?? i18nAdvantages.FR;

  /* =========================
     Effects
     ========================= */
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === 0 ? 1 : 0));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem("lastVisitDate");
    if (lastVisitDate !== today) {
      saveVisite();
      localStorage.setItem("lastVisitDate", today);
    }
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        if (response && response.length > 0) setSettings(response[0]);
        else console.error("Aucune donnée trouvée dans la table settings.");
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchAboutInfo = async () => {
      try {
        const response = await getAboutInfo();
        setAboutInfo(response);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des informations :",
          error,
        );
      }
    };
    fetchAboutInfo();
  }, []);

  /* =========================
     UI helpers (cohérence)
     ========================= */
  const sectionWrap =
    "relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8";
  const softCard =
    "rounded-2xl border border-black/10 bg-[#E8BB89]/80 shadow-sm " +
    "dark:bg-neutral-950/70 dark:border-white/10 dark:shadow-none";
  const softText = "text-gray-800 dark:text-gray-100";
  const mutedText = "text-gray-700 dark:text-gray-300";
  const buttonPrimary =
    "inline-flex items-center justify-center rounded-2xl bg-primary px-6 py-3 text-white shadow " +
    "hover:opacity-95 transition active:scale-[0.99]";
  const buttonPrimarySmall =
    "inline-flex items-center justify-center rounded-2xl bg-primary px-5 py-2.5 text-white shadow " +
    "hover:opacity-95 transition active:scale-[0.99]";

  /* =========================
   About content splitting
   ========================= */

  const ABOUT_MARKERS = {
    FR: [
      {
        title: "Qui suis-je ?",
        aliases: ["Qui suis-je ?", "Qui suis-je?"],
      },
      {
        title: "C’est quoi ?",
        aliases: ["C'est quoi ?", "C’est quoi ?", "C est quoi ?"],
      },
      {
        title: "Ma spécialité",
        aliases: ["Ma spécialité", "Ma specialite"],
      },
      {
        title: "Pourquoi venir découvrir le Zen Shiatsu ?",
        aliases: [
          "Pourquoi venir découvrir le Zen Shiatsu ?",
          "Pourquoi venir decouvrir le Zen Shiatsu ?",
          "Pourquoi venir découvrir le Zen Shiatsu",
        ],
      },
    ],
    EN: [
      {
        title: "Who am I?",
        aliases: ["Who am I?", "About me"],
      },
      {
        title: "What is it?",
        aliases: ["What is it?", "What is Zen Shiatsu?"],
      },
      {
        title: "My speciality",
        aliases: ["My speciality", "My specialty"],
      },
      {
        title: "Why discover Zen Shiatsu?",
        aliases: ["Why discover Zen Shiatsu?", "Why try Zen Shiatsu?"],
      },
    ],
  };

  const normalizeMarker = (value = "") =>
    String(value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[’']/g, " ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const nodeToHtml = (node) => {
    if (!node) return "";
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
    return node.outerHTML || node.textContent || "";
  };

  const splitHtmlByMarkers = (html, markers = []) => {
    const safeHtml = DOMPurify.sanitize(String(html || ""));

    if (!safeHtml) {
      return [];
    }

    if (!markers?.length) {
      return [
        {
          title: language === "FR" ? "Présentation" : "Overview",
          html: safeHtml,
        },
      ];
    }

    const root = document.createElement("div");
    root.innerHTML = safeHtml;

    const findMarker = (text = "") => {
      const normalizedText = normalizeMarker(text);

      return markers.find((marker) =>
        marker.aliases.some((alias) =>
          normalizedText.includes(normalizeMarker(alias)),
        ),
      );
    };

    const isOnlyMarkerNode = (text = "", marker) => {
      const normalizedText = normalizeMarker(text);
      return marker.aliases.some(
        (alias) => normalizedText === normalizeMarker(alias),
      );
    };

    const sections = [];
    let currentSection = null;

    const pushCurrentSection = () => {
      if (!currentSection) return;

      const sectionHtml = currentSection.htmlParts.join("").trim();

      sections.push({
        title: currentSection.title,
        html: sectionHtml,
      });
    };

    Array.from(root.childNodes).forEach((node) => {
      const text = node.textContent || "";
      const detectedMarker = findMarker(text);

      if (detectedMarker) {
        pushCurrentSection();

        currentSection = {
          title: detectedMarker.title,
          htmlParts: [],
        };

        // Si le noeud contient uniquement le titre du panneau,
        // on ne le remet pas dans le contenu pour éviter les doublons.
        if (!isOnlyMarkerNode(text, detectedMarker)) {
          currentSection.htmlParts.push(nodeToHtml(node));
        }

        return;
      }

      if (!currentSection) {
        currentSection = {
          title: language === "FR" ? "Présentation" : "Overview",
          htmlParts: [],
        };
      }

      currentSection.htmlParts.push(nodeToHtml(node));
    });

    pushCurrentSection();

    // Si aucun marqueur n’a été détecté, fallback propre.
    if (sections.length <= 1 && sections[0]?.title === "Présentation") {
      return [
        {
          title: language === "FR" ? "Présentation" : "Overview",
          html: safeHtml,
        },
      ];
    }

    return sections.filter((section) => section.html?.trim() || section.title);
  };

  const aboutRaw =
    settings?.[getLocalizedKey("about_content")] || getFallbackMessage("text");

  const aboutSections = useMemo(
    () =>
      splitHtmlByMarkers(aboutRaw, ABOUT_MARKERS[language] || ABOUT_MARKERS.FR),
    [aboutRaw, language],
  );

  const [openAboutIndex, setOpenAboutIndex] = useState(0);

  /* =========================
     Modal
     ========================= */
  const openModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  /* =========================
     Loading
     ========================= */
  if (!settings || !aboutInfo) {
    return (
      <div className="text-center py-12 text-gray-700 dark:text-gray-200">
        <p>
          {language === "FR" ? "Chargement des données..." : "Loading data..."}
        </p>
      </div>
    );
  }

  const prettyTitle = (rawTitle) => {
    const fallback = language === "FR" ? "Présentation" : "Overview";
    const t = String(rawTitle || "").trim();
    if (!t) return fallback;

    // Version “propre” : on coupe au ":" si présent et on limite un peu
    const short = t.includes(":") ? t.split(":")[0].trim() : t;
    return short.length > 48 ? short.slice(0, 48).trim() + "…" : short;
  };

  return (
    <div className="relative min-h-screen flex flex-col gap-10 pb-10">
      {/* Fond décor global */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-0 left-0 w-full h-[520px] bg-cover bg-no-repeat bg-bottom opacity-70 dark:opacity-35"
          style={{ backgroundImage: "url('/images/decor_003.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent dark:from-black/60" />
      </div>

      {/* HERO */}
      <section className="relative isolate w-full overflow-hidden pt-20 sm:pt-24">
        <div className="relative h-[70vh] min-h-[520px] sm:min-h-[560px] w-full overflow-hidden">
          {[settings.header1_photo_url, settings.header2_photo_url].map(
            (url, index) => (
              <div
                key={index}
                className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
                  index === currentSlide ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  backgroundImage: `url('${
                    isExternalURL(url) ? url : `${BASE_URL}${url}`
                  }')`,
                }}
              />
            ),
          )}

          <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/55 via-black/35 to-black/60" />

          <div
            className={`relative z-20 h-full ${sectionWrap} flex items-center`}
          >
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-white/90 backdrop-blur-md border border-white/15">
                <span className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-sm font-satoshi">
                  {language === "FR"
                    ? "Prévention • Bien-être"
                    : "Prevention • Well-being"}
                </span>
              </div>

              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-satoshi drop-shadow-lg leading-tight">
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      settings[getLocalizedKey("header_title")] ||
                        getFallbackMessage("title"),
                    ),
                  }}
                />
              </h1>

              <div
                className="
                  mt-5 max-w-2xl rounded-2xl
                  bg-white/15 backdrop-blur-md
                  border border-white/15
                  px-5 py-4 text-white/90
                  shadow-[0_10px_30px_rgba(0,0,0,0.25)]
                "
              >
                <div
                  className="text-base sm:text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      settings[getLocalizedKey("header_subtitle")] ||
                        getFallbackMessage("text"),
                    ),
                  }}
                />
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a href="/prise_rdv" className={buttonPrimary}>
                  {language === "FR"
                    ? "Prendre rendez-vous"
                    : "Make an appointment"}
                </a>
                <a
                  href="/about"
                  className="
                    inline-flex items-center justify-center rounded-2xl
                    bg-white/10 px-6 py-3 text-white
                    border border-white/20 backdrop-blur-md
                    hover:bg-white/15 transition
                  "
                >
                  {language === "FR" ? "En savoir plus" : "Learn more"}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUI SUIS-JE */}
      <section className={sectionWrap}>
        <div className="py-6 sm:py-10">
          <div className="text-center mb-10 sm:mb-12">
            {/* petit badge au-dessus */}
            <div
              className="inline-flex items-center gap-2 rounded-full px-4 py-2
                  bg-black/5 dark:bg-white/10
                  border border-black/10 dark:border-white/10
                  backdrop-blur-sm"
            >
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
                {language === "FR" ? "Qui suis-je ?" : "About"}
              </span>
            </div>

            {/* titre */}
            {/* <h2
    className={`
      mt-4 text-3xl sm:text-4xl lg:text-5xl
      font-satoshi font-extrabold tracking-tight
      ${softText}
    `}
  >
    {settings[getLocalizedKey("about_title")] || getFallbackMessage("title")}
  </h2> */}

            {/* underline moderne */}
            <div className="mt-4 flex justify-center">
              <div className="h-[3px] w-24 sm:w-28 rounded-full bg-primary/90" />
            </div>

            {/* petit sous-texte optionnel (si tu veux) */}
            <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-gray-700 dark:text-gray-300">
              {language === "FR"
                ? "Un accompagnement doux et profond pour retrouver équilibre, énergie et sérénité."
                : "A gentle and deep support to regain balance, energy and serenity."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5 lg:col-span-4 flex justify-center md:justify-start md:sticky md:top-24 self-start">
              <img
                src={
                  isExternalURL(settings.logo_club)
                    ? settings.logo_club
                    : `${BASE_URL}${
                        settings.logo_club || "/images/default_logo.webp"
                      }`
                }
                alt="Logo Gaëlle Prevention Shiatsu"
                className="w-56 sm:w-64 md:w-full max-w-[320px] rounded-2xl dark:shadow-none"
              />
            </div>

            <div
              className={`md:col-span-7 lg:col-span-8 ${softCard} p-5 sm:p-6`}
            >
              <div className="space-y-3">
                {aboutSections.map((sec, idx) => {
                  const open = idx === openAboutIndex;

                  return (
                    <div
                      key={idx}
                      className="
                        rounded-2xl border border-black/10 dark:border-white/10
                        bg-white/35 dark:bg-white/5
                        overflow-hidden
                      "
                    >
                      <button
                        type="button"
                        onClick={() => setOpenAboutIndex(open ? -1 : idx)}
                        className="
                          w-full flex items-center justify-between gap-4
                          px-4 py-4 text-left
                          hover:text-primary transition-colors
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
                        "
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {/* puce accent */}
                          <span
                            className={`
      h-2.5 w-2.5 rounded-full bg-primary
      shadow-[0_0_0_4px_rgba(242,113,49,0.18)]
      transition-all duration-200
      ${open ? "scale-110" : "scale-100"}
    `}
                            aria-hidden="true"
                          />

                          {/* titre */}
                          <div className="min-w-0">
                            <div
                              className={`
        font-satoshi font-semibold tracking-tight
        text-base sm:text-lg
        ${softText}
        transition-all duration-200
        ${open ? "text-primary" : ""}
        truncate
      `}
                              title={
                                sec.title ||
                                (language === "FR"
                                  ? "Présentation"
                                  : "Overview")
                              }
                            >
                              {sec.title || prettyTitle(sec.title)}
                            </div>

                            {/* sous-ligne moderne (petit trait animé) */}
                            <div
                              className={`
        mt-1 h-[2px] w-16 rounded-full bg-primary
        origin-left transition-transform duration-300 ease-out
        ${open ? "scale-x-100" : "scale-x-0"}
      `}
                              aria-hidden="true"
                            />
                          </div>
                        </div>

                        <span
                          className={`
                            shrink-0 h-9 w-9 rounded-2xl flex items-center justify-center
                            bg-black/5 dark:bg-white/10
                            text-primary
                            transition-transform duration-200
                            ${open ? "rotate-45" : "rotate-0"}
                          `}
                          aria-hidden="true"
                        >
                          +
                        </span>
                      </button>

                      <div
                        className={`
                          grid transition-all duration-300 ease-out
                          ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}
                        `}
                      >
                        <div className="overflow-hidden">
                          <div className="px-4 pb-4">
                            <div
                              className={`prose prose-neutral max-w-none dark:prose-invert ${mutedText}`}
                              dangerouslySetInnerHTML={{ __html: sec.html }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6">
                <a href="/about" className={buttonPrimarySmall}>
                  {language === "FR"
                    ? "Découvrir mon parcours"
                    : "Discover my journey"}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SakuraSeparator />

      {/* Diaporama */}
      <section className={sectionWrap}>
        <div className="py-2">
          <Diaporama />
        </div>
      </section>

      <SakuraSeparator />

      {/* AVANTAGES */}
      <section className={sectionWrap}>
        <div className="py-8 sm:py-12">
          {/* Title modern */}
          <div className="text-center mb-10">
            <div
              className="
          inline-flex items-center gap-2 rounded-full px-4 py-2
          bg-black/5 dark:bg-white/10
          border border-black/10 dark:border-white/10
          backdrop-blur-sm
        "
            >
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
                {language === "FR" ? "Bienfaits" : "Benefits"}
              </span>
            </div>

            <h2
              className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-satoshi font-extrabold tracking-tight ${softText}`}
            >
              {adv.title}
            </h2>

            <div className="mt-4 flex justify-center">
              <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
            </div>

            <p className="mt-4 max-w-3xl mx-auto text-sm sm:text-base text-gray-700 dark:text-gray-300">
              {language === "FR"
                ? "Une approche douce et profonde pour relâcher les tensions, retrouver votre énergie et renforcer votre équilibre au quotidien."
                : "A gentle and deep approach to release tension, regain energy, and strengthen your daily balance."}
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {adv.cards.map((card, idx) => {
              const icon = ["✦", "❖", "✺"][idx % 3]; // simple, efficace (pas besoin d’icônes importées)
              return (
                <div
                  key={idx}
                  className={`
              relative overflow-hidden
              rounded-3xl p-6 sm:p-7
              border border-black/10 dark:border-white/10
              bg-[#E8BB89]/80 dark:bg-white/5
              backdrop-blur-sm
              shadow-sm dark:shadow-none
              transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg
            `}
                >
                  {/* subtle highlight */}
                  <div className="pointer-events-none absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-primary/15 blur-2xl" />
                  </div>

                  <div className="relative">
                    {/* Icon + title */}
                    <div className="flex items-start justify-center gap-3 text-center">
                      <div
                        className="
                    shrink-0 h-11 w-11 rounded-2xl
                    bg-primary/15 text-primary
                    flex items-center justify-center
                    text-lg font-bold text-white
                    border border-primary/20
                  "
                        aria-hidden="true"
                      >
                        {icon}
                      </div>
                    </div>

                    <h3
                      className={`mt-4 text-lg sm:text-xl font-satoshi font-bold text-center ${softText}`}
                    >
                      {card.title}
                    </h3>

                    <div className="mt-3 flex justify-center">
                      <div className="h-[2px] w-16 rounded-full bg-primary/70" />
                    </div>

                    <p
                      className={`mt-4 text-sm sm:text-base leading-relaxed text-center ${mutedText}`}
                    >
                      {card.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-10 sm:mt-12 flex flex-col items-center gap-4">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 text-center max-w-2xl">
              {language === "FR"
                ? "Envie d’essayer ? Prenez rendez-vous et offrez-vous un moment de recentrage."
                : "Want to try? Book an appointment and give yourself a moment to reset."}
            </p>
            <a href="/prise_rdv" className={buttonPrimary}>
              {language === "FR"
                ? "Prendre rendez-vous"
                : "Make an appointment"}
            </a>
          </div>
        </div>
      </section>

      <SakuraSeparator />

      {/* MON PROJET */}
      <section className={sectionWrap}>
        <div className="py-6 sm:py-10">
          {/* Header section (optionnel mais très premium) */}
          {/* <div className="mb-8 sm:mb-10 text-center">
      <div
        className="
          inline-flex items-center gap-2 rounded-full px-4 py-2
          bg-black/5 dark:bg-white/10
          border border-black/10 dark:border-white/10
          backdrop-blur-sm
        "
      >
        <span className="h-2 w-2 rounded-full bg-primary" />
        <span className="text-xs sm:text-sm font-satoshi font-semibold tracking-wide text-gray-700 dark:text-gray-200">
          {language === "FR" ? "Mon projet" : "My project"}
        </span>
      </div>

      <div className="mt-4 flex justify-center">
        <div className="h-[3px] w-24 sm:w-32 rounded-full bg-primary/90" />
      </div>
    </div> */}

          <div
            className="
        rounded-3xl overflow-hidden
        border border-black/10 dark:border-white/10
        bg-[#E8BB89]/80 dark:bg-white/5
        backdrop-blur-sm
        shadow-sm
      "
          >
            <div className="grid grid-cols-1 lg:grid-cols-12">
              {/* IMAGE */}
              <div className="lg:col-span-5 p-5 sm:p-6">
                <button
                  type="button"
                  onClick={() => openModal(aboutInfo.projet_image)}
                  className="
              group relative w-full overflow-hidden rounded-3xl
              border border-black/10 dark:border-white/10
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
            "
                  aria-label={
                    language === "FR" ? "Agrandir l'image" : "Open image"
                  }
                >
                  <img
                    src={
                      isExternalURL(aboutInfo.projet_image)
                        ? aboutInfo.projet_image
                        : `${BASE_URL}${aboutInfo.projet_image}`
                    }
                    alt={language === "FR" ? "Mon projet" : "My project"}
                    className="
                h-[260px] sm:h-[340px] lg:h-[460px] w-full object-cover
                transition-transform duration-500 ease-out
                group-hover:scale-[1.03]
              "
                    loading="lazy"
                  />

                  {/* Overlay + call to action */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent opacity-80" />

                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="text-white">
                      <div className="text-sm font-semibold">
                        {language === "FR" ? "Aperçu" : "Preview"}
                      </div>
                      <div className="text-xs text-white/80">
                        {language === "FR"
                          ? "Cliquer pour agrandir"
                          : "Click to enlarge"}
                      </div>
                    </div>

                    <div
                      className="
                  inline-flex items-center gap-2
                  rounded-2xl px-3 py-2
                  bg-white/15 backdrop-blur-md
                  border border-white/15
                  text-white text-sm
                  transition
                  group-hover:bg-white/20
                "
                    >
                      <span className="text-base">⤢</span>
                      <span className="hidden sm:inline">
                        {language === "FR" ? "Agrandir" : "Open"}
                      </span>
                    </div>
                  </div>
                </button>
              </div>

              {/* TEXTE */}
              <div className="lg:col-span-7 p-5 sm:p-6 lg:p-8">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-satoshi font-extrabold tracking-tight text-gray-900 dark:text-white">
                  {aboutInfo[getLocalizedKey("projet_title")] ||
                    getFallbackMessage("title")}
                </h2>

                <div className="mt-4 h-[3px] w-20 rounded-full bg-primary/80" />

                <div
                  className={`mt-5 prose prose-neutral max-w-none dark:prose-invert ${mutedText}`}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      aboutInfo[getLocalizedKey("projet_text")] ||
                        getFallbackMessage("text"),
                    ),
                  }}
                />

                {/* CTA optionnel en bas (plus vivant) */}
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/prise_rdv"
                    className="
                inline-flex items-center justify-center rounded-2xl
                bg-primary px-5 py-3 text-white shadow
                hover:opacity-95 transition active:scale-[0.99]
              "
                  >
                    {language === "FR"
                      ? "Prendre rendez-vous"
                      : "Make an appointment"}
                  </a>

                  <a
                    href="/about"
                    className="
                inline-flex items-center justify-center rounded-2xl
                bg-black/5 dark:bg-white/10
                px-5 py-3
                text-gray-900 dark:text-white
                border border-black/10 dark:border-white/10
                hover:border-primary/60 transition
              "
                  >
                    {language === "FR" ? "En savoir plus" : "Learn more"}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SakuraSeparator />

      {/* Dernières actualités */}
      <section className={sectionWrap}>
        <LastNews />
      </section>

      {/* <SakuraSeparator /> */}

      {/* Derniers albums photos */}
      {/* <section className={sectionWrap}>
        <LastGallerie />
      </section> */}

      <SakuraSeparator />

      <section className={sectionWrap}>
        <ChateauErigoyeSection />
      </section>

      <SakuraSeparator />

      <section className={sectionWrap}>
        <TestimonialsSection />
      </section>

      <SakuraSeparator />

      <section className={sectionWrap}>
        <PartenairesCarousel />
      </section>

      <section className={sectionWrap}>
        <div className="pt-6 flex justify-center">
          <a href="/cgv" className={buttonPrimary}>
            {language === "FR"
              ? "Conditions générales de vente"
              : "Terms and Conditions"}
          </a>
        </div>
      </section>

      {/* Modal */}
      {isModalOpen && selectedImage && (
        <div
          className="fixed inset-0 z-[999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <img
            src={
              isExternalURL(selectedImage)
                ? selectedImage
                : `${BASE_URL}${selectedImage}`
            }
            alt="Aperçu"
            className="max-h-[85vh] max-w-[92vw] rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default HomePage;
